import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Users, FileText, CheckCircle, Clock, Award, MapPin, Zap } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';
const COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6'];

function ClientAnalytics({ token, requirements }) {
  const total = requirements.length;
  const active = requirements.filter(r => r.status === 'live').length;

  const stats = [
    { icon: '📋', label: 'Total Requirements', value: total, color: '#6366f1', bg: '#ede9fe' },
    { icon: '✅', label: 'Active Requirements', value: active, color: '#16a34a', bg: '#dcfce7' },
    { icon: '👥', label: 'Candidates Received', value: '—', color: '#f59e0b', bg: '#fef9c3' },
    { icon: '📅', label: 'Interviews Scheduled', value: '—', color: '#8b5cf6', bg: '#ede9fe' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '16px', marginBottom: '24px' }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: '12px', padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', flexShrink: 0 }}>{s.icon}</div>
            <div>
              <div style={{ color: '#1e293b', fontSize: '36px', fontWeight: '800', lineHeight: 1 }}>{s.value}</div>
              <div style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: 'linear-gradient(135deg,#ede9fe,#f8f7ff)', borderRadius: '12px', padding: '28px', border: '1px solid #c4b5fd', textAlign: 'center' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
        <h3 style={{ color: '#6366f1', margin: '0 0 8px', fontWeight: '700' }}>Detailed Analytics Coming Soon!</h3>
        <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Track your hiring pipeline, candidate scores, and more.</p>
      </div>
    </div>
  );
}

export default function Analytics({ token, userRole, setActive }) {
  const [requirements, setRequirements] = useState([]);
  const [bench, setBench] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const reqUrl = userRole === 'vendor' ? `${API}/requirements/live` : userRole === 'client' ? `${API}/requirements/my` : `${API}/requirements`;
      const benchUrl = userRole === 'vendor' ? `${API}/bench/my` : `${API}/bench`;
      const [reqRes, benchRes] = await Promise.all([
        fetch(reqUrl, { headers }),
        fetch(benchUrl, { headers })
      ]);
      const reqData = await reqRes.json();
      const benchData = await benchRes.json();
      if (reqData.success) setRequirements(reqData.requirements || []);
      if (benchData.success) setBench(benchData.resources || []);
    } catch {}
    setLoading(false);
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: '#94a3b8' }}>Loading analytics...</div>;

  if (userRole === "vendor") return (<div><div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"16px",marginBottom:"24px"}}>{[{icon:"👥",label:"Total Bench Resources",value:bench.length},{icon:"⚡",label:"Immediately Available",value:bench.filter(b=>b.availability==="immediate").length},{icon:"💼",label:"Live Jobs Available",value:requirements.length},{icon:"📤",label:"Candidates Submitted",value:"—"}].map(s=>(<div key={s.label} style={{background:"white",borderRadius:"12px",padding:"28px",border:"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:"20px"}}><div style={{width:"56px",height:"56px",borderRadius:"14px",background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"28px"}}>{s.icon}</div><div><div style={{color:"#1e293b",fontSize:"36px",fontWeight:"800"}}>{s.value}</div><div style={{color:"#64748b",fontSize:"14px",marginTop:"4px"}}>{s.label}</div></div></div>))}</div></div>);

  if (userRole === 'client') return <ClientAnalytics token={token} requirements={requirements} />;

  // Admin & Vendor full analytics
  const jobSkillsMap = {};
  requirements.forEach(r => (r.skills || []).forEach(s => { jobSkillsMap[s] = (jobSkillsMap[s] || 0) + 1; }));
  const topJobSkills = Object.entries(jobSkillsMap).sort((a,b) => b[1]-a[1]).slice(0,8).map(([name,value]) => ({ name, value }));

  const benchSkillsMap = {};
  bench.forEach(b => (b.skills || []).forEach(s => { benchSkillsMap[s] = (benchSkillsMap[s] || 0) + 1; }));
  const topBenchSkills = Object.entries(benchSkillsMap).sort((a,b) => b[1]-a[1]).slice(0,8).map(([name,value]) => ({ name, value }));

  const allSkills = [...new Set([...Object.keys(jobSkillsMap), ...Object.keys(benchSkillsMap)])].slice(0,8);
  const demandVsSupply = allSkills.map(skill => ({
    name: skill,
    demand: jobSkillsMap[skill] || 0,
    supply: benchSkillsMap[skill] || 0,
  }));

  const jobLocationMap = {};
  requirements.forEach(r => { const loc = r.location || 'Remote'; jobLocationMap[loc] = (jobLocationMap[loc] || 0) + 1; });
  const jobsByLocation = Object.entries(jobLocationMap).sort((a,b) => b[1]-a[1]).slice(0,6).map(([name,value]) => ({ name, value }));

  const benchLocationMap = {};
  bench.forEach(b => { const loc = b.location || 'Remote'; benchLocationMap[loc] = (benchLocationMap[loc] || 0) + 1; });
  const benchByLocation = Object.entries(benchLocationMap).sort((a,b) => b[1]-a[1]).slice(0,6).map(([name,value]) => ({ name, value }));

  const statusData = [
    { name: 'Pending', value: requirements.filter(r => r.status === 'pending').length, color: '#f59e0b' },
    { name: 'Live', value: requirements.filter(r => r.status === 'live').length, color: '#10b981' },
    { name: 'Closed', value: requirements.filter(r => r.status === 'closed').length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const availData = [
    { name: 'Immediate', value: bench.filter(b => b.availability === 'immediate').length, color: '#10b981' },
    { name: '15 Days', value: bench.filter(b => b.availability === '15days').length, color: '#6366f1' },
    { name: '30 Days', value: bench.filter(b => b.availability === '30days').length, color: '#f59e0b' },
    { name: '60 Days', value: bench.filter(b => b.availability === '60days').length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const totalDemand = requirements.length;
  const totalSupply = bench.length;
  const matchPercent = totalDemand > 0 ? Math.min(Math.round((totalSupply / totalDemand) * 100), 100) : 0;

  const stats = [
    { icon: FileText, label: 'Total Requirements', value: requirements.length, color: '#6366f1', bg: '#ede9fe', nav: 'requirements' },
    { icon: CheckCircle, label: 'Live Jobs', value: requirements.filter(r => r.status === 'live').length, color: '#16a34a', bg: '#dcfce7', nav: 'requirements' },
    { icon: Users, label: 'Bench Resources', value: bench.length, color: '#7c3aed', bg: '#ede9fe', nav: 'bench' },
    { icon: Award, label: 'Immediate Available (Bench)', value: bench.filter(b => b.availability === 'immediate').length, color: '#10b981', bg: '#dcfce7', nav: 'bench' },
    { icon: Zap, label: 'Supply vs Demand', value: `${matchPercent}%`, color: '#f59e0b', bg: '#fef9c3', nav: null },
    { icon: TrendingUp, label: 'Avg Budget', value: requirements.length ? `Rs.${Math.round(requirements.reduce((a,b) => a+(b.budget||0),0)/requirements.length).toLocaleString()}` : 'N/A', color: '#3b82f6', bg: '#dbeafe', nav: null },
  ];

  const card = { background:'white',borderRadius:'12px',padding:'24px',border:'1px solid #e2e8f0',boxShadow:'0 1px 4px rgba(0,0,0,0.04)',marginBottom:'20px' };
  const empty = { height:'200px',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8',fontSize:'14px' };

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'16px',marginBottom:'24px'}}>
        {stats.map(s => { const Icon = s.icon; return (
          <div key={s.label} style={{background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',boxShadow:'0 1px 4px rgba(0,0,0,0.04)',overflow:'hidden'}}>
          <button onClick={() => { if(s.nav && setActive) setActive(s.nav); }} style={{width:'100%',background:'none',border:'none',padding:'20px',cursor:s.nav?'pointer':'default',textAlign:'left',fontFamily:'inherit',transition:'all 0.2s'}}
            onMouseEnter={e => { if(s.nav) e.currentTarget.style.background='#f8fafc'; }}
            onMouseLeave={e => { e.currentTarget.style.background='none'; }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
              <div style={{width:'40px',height:'40px',borderRadius:'10px',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon size={20} color={s.color}/></div>
              {s.nav && <span style={{fontSize:'10px',color:'#94a3b8',fontWeight:'500'}}>View →</span>}
            </div>
            <div style={{color:'#1e293b',fontSize:'24px',fontWeight:'700',marginBottom:'4px'}}>{s.value}</div>
            <div style={{color:'#94a3b8',fontSize:'13px'}}>{s.label}</div>
          </button></div>
        );})}
      </div>

      <div style={card}>
        <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Job Demand vs Bench Availability</h3>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'16px',alignItems:'center'}}>
          <div style={{textAlign:'center',padding:'20px',background:'#ede9fe',borderRadius:'10px'}}>
            <div style={{color:'#6366f1',fontSize:'36px',fontWeight:'800'}}>{totalDemand}</div>
            <div style={{color:'#64748b',fontSize:'13px',marginTop:'4px'}}>Job Demand</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{background:'#f1f5f9',borderRadius:'20px',height:'12px',overflow:'hidden',marginBottom:'8px'}}>
              <div style={{background:'linear-gradient(135deg,#6366f1,#10b981)',height:'100%',width:`${matchPercent}%`,borderRadius:'20px'}}/>
            </div>
            <div style={{color:'#1e293b',fontSize:'28px',fontWeight:'800'}}>{matchPercent}%</div>
            <div style={{color:'#64748b',fontSize:'12px'}}>Match Rate</div>
          </div>
          <div style={{textAlign:'center',padding:'20px',background:'#dcfce7',borderRadius:'10px'}}>
            <div style={{color:'#16a34a',fontSize:'36px',fontWeight:'800'}}>{totalSupply}</div>
            <div style={{color:'#64748b',fontSize:'13px',marginTop:'4px'}}>Bench Available</div>
          </div>
        </div>
      </div>

      <div style={card}>
        <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Job Demand vs Bench Supply by Skills</h3>
        {demandVsSupply.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={demandVsSupply}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="name" tick={{fontSize:12,fill:'#64748b'}}/>
              <YAxis tick={{fontSize:12,fill:'#64748b'}}/>
              <Tooltip/><Legend/>
              <Bar dataKey="demand" name="Job Demand" fill="#6366f1" radius={[4,4,0,0]}/>
              <Bar dataKey="supply" name="Bench Supply" fill="#10b981" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        ) : <div style={empty}>Add requirements and bench resources to see data</div>}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Top Job Skills</h3>
          {topJobSkills.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topJobSkills} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis type="number" tick={{fontSize:12,fill:'#64748b'}}/>
                <YAxis dataKey="name" type="category" tick={{fontSize:12,fill:'#64748b'}} width={80}/>
                <Tooltip/>
                <Bar dataKey="value" fill="#6366f1" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No job requirements yet</div>}
        </div>

        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Top Bench Skills</h3>
          {topBenchSkills.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topBenchSkills} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis type="number" tick={{fontSize:12,fill:'#64748b'}}/>
                <YAxis dataKey="name" type="category" tick={{fontSize:12,fill:'#64748b'}} width={80}/>
                <Tooltip/>
                <Bar dataKey="value" fill="#8b5cf6" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No bench resources yet</div>}
        </div>

        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px',display:'flex',alignItems:'center',gap:'6px'}}><MapPin size={15} color="#6366f1"/> Jobs by Location</h3>
          {jobsByLocation.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={jobsByLocation} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value}) => `${name}: ${value}`}>
                  {jobsByLocation.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No location data yet</div>}
        </div>

        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px',display:'flex',alignItems:'center',gap:'6px'}}><MapPin size={15} color="#8b5cf6"/> Bench by Location</h3>
          {benchByLocation.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={benchByLocation} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value}) => `${name}: ${value}`}>
                  {benchByLocation.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No location data yet</div>}
        </div>

        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Requirements Status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value}) => `${name}: ${value}`}>
                  {statusData.map((entry,i) => <Cell key={i} fill={entry.color}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No requirements yet</div>}
        </div>

        <div style={card}>
          <h3 style={{color:'#1e293b',fontSize:'15px',fontWeight:'600',margin:'0 0 16px'}}>Bench Availability</h3>
          {availData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={availData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value}) => `${name}: ${value}`}>
                  {availData.map((entry,i) => <Cell key={i} fill={entry.color}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={empty}>No bench resources yet</div>}
        </div>
      </div>
    </div>
  );
}
